import React, { useEffect, useState } from "react";
import { Button, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";

import { Aviso, EstadoTabela, ModalHub, fieldInputStyle } from "../_documentosFrete/comum";
import { CAMPOS_OPERACIONAIS, atualizarOperacional, errosDeCampos, mensagemErroPedido, obterDetalhe } from "../../../services/pedidosApi";

// Editar dados operacionais do pedido (H2, PO1-2376): PUT /pedidos/operacional.
// A linha em `Pedido` nasce aqui se ainda não existir (upsert no backend). A NF
// não muda. '' limpa o campo (vira NULL no servidor).

export const ROTULOS = {
  codigoRastreio: "Código de rastreio",
  qtdVolumes: "Volumes",
  peso: "Peso (kg)",
  altura: "Altura (cm)",
  largura: "Largura (cm)",
  comprimento: "Comprimento (cm)",
  contatoNome: "Nome do contato",
  contatoTelefone: "Telefone",
  contatoEmail: "E-mail",
  observacao: "Observação",
};

const NUMERICOS = ["qtdVolumes", "peso", "altura", "largura", "comprimento"];

export const formVazio = () => CAMPOS_OPERACIONAIS.reduce((acc, k) => ({ ...acc, [k]: "" }), {});

// Linha do Pedido → form (strings; número com vírgula para o usuário).
export function formDoPedido(p) {
  const base = formVazio();
  if (!p) return base;
  CAMPOS_OPERACIONAIS.forEach((k) => {
    const v = p[k];
    if (v === null || v === undefined) return;
    base[k] = NUMERICOS.indexOf(k) !== -1 ? String(v).replace(".", ",") : String(v);
  });
  return base;
}

// Validação local (mesmas regras do Yup do backend).
export function validarForm(form) {
  const erros = {};
  NUMERICOS.forEach((k) => {
    const bruto = String(form[k] || "").trim();
    if (!bruto) return;
    const n = Number(bruto.replace(",", "."));
    if (!Number.isFinite(n) || n < 0) erros[k] = "Informe um número maior ou igual a zero";
    else if (k === "qtdVolumes" && !Number.isInteger(n)) erros[k] = "Volumes deve ser um número inteiro";
  });
  const email = String(form.contatoEmail || "").trim();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) erros.contatoEmail = "E-mail inválido";
  if (String(form.codigoRastreio || "").length > 50) erros.codigoRastreio = "Até 50 caracteres";
  if (String(form.contatoNome || "").length > 120) erros.contatoNome = "Até 120 caracteres";
  if (String(form.contatoTelefone || "").length > 30) erros.contatoTelefone = "Até 30 caracteres";
  if (String(form.contatoEmail || "").length > 120) erros.contatoEmail = "Até 120 caracteres";
  if (String(form.observacao || "").length > 500) erros.observacao = "Até 500 caracteres";
  return erros;
}

// Form → body do PUT: só os campos que mudaram em relação ao registro carregado.
export function payloadDoForm(form, original) {
  const corpo = {};
  CAMPOS_OPERACIONAIS.forEach((k) => {
    const atual = String(form[k] === undefined || form[k] === null ? "" : form[k]).trim();
    const antes = String(original && original[k] !== undefined && original[k] !== null ? original[k] : "").trim();
    if (atual === antes) return;
    if (NUMERICOS.indexOf(k) !== -1) corpo[k] = atual === "" ? "" : atual.replace(",", ".");
    else corpo[k] = atual;
  });
  return corpo;
}

export default function PedidoOperacionalModal({ pedido, onClose }) {
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState("");
  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState(formVazio);
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [alterou, setAlterou] = useState(false);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErroCarga("");
    obterDetalhe(pedido)
      .then((d) => {
        if (!ativo) return;
        const p = (d && d.pedido) || null;
        setOriginal(formDoPedido(p));
        setForm(formDoPedido(p));
      })
      .catch((e) => ativo && setErroCarga(mensagemErroPedido(e, "Não conseguimos carregar os dados do pedido.")))
      .finally(() => ativo && setCarregando(false));
    return () => {
      ativo = false;
    };
  }, [pedido]);

  // React 16: o valor é lido do evento ANTES do updater (event pooling).
  const set = (campo, valor) => {
    setForm((atual) => ({ ...atual, [campo]: valor }));
    setErros((atual) => (atual[campo] ? { ...atual, [campo]: undefined } : atual));
  };

  const salvar = async () => {
    if (salvando || carregando) return;
    const locais = validarForm(form);
    if (Object.keys(locais).length) {
      setErros(locais);
      setAviso({ tom: "erro", texto: `Confira os campos: ${Object.keys(locais).map((k) => ROTULOS[k] || k).join(", ")}.` });
      return;
    }
    const corpo = payloadDoForm(form, original);
    if (!Object.keys(corpo).length) {
      setAviso({ tom: "info", texto: "Nada mudou." });
      return;
    }
    setSalvando(true);
    setErros({});
    setAviso(null);
    try {
      const r = await atualizarOperacional(pedido, corpo);
      const alterados = (r && r.alterados) || [];
      setOriginal(formDoPedido(r && r.pedido));
      setForm(formDoPedido(r && r.pedido));
      setAlterou(true);
      setAviso({
        tom: "ok",
        texto: alterados.length ? `Dados salvos: ${alterados.map((k) => ROTULOS[k] || k).join(", ")}.` : "Nada mudou.",
      });
    } catch (e) {
      setErros(errosDeCampos(e));
      setAviso({ tom: "erro", texto: mensagemErroPedido(e, "Não foi possível salvar os dados operacionais.") });
    } finally {
      setSalvando(false);
    }
  };

  const fechar = () => {
    if (salvando) return;
    onClose(alterou);
  };

  const campo = (nome, controle, md) => (
    <Col md={md}>
      <FormGroup>
        <Label for={`pedido-op-${nome}`}>{ROTULOS[nome]}</Label>
        {controle}
        {erros[nome] ? <span className="docs-frete__erro-campo">{erros[nome]}</span> : null}
      </FormGroup>
    </Col>
  );

  const input = (nome, props = {}) => (
    <Input
      id={`pedido-op-${nome}`}
      value={form[nome]}
      onChange={(e) => set(nome, e.target.value)}
      disabled={carregando || salvando}
      invalid={Boolean(erros[nome])}
      style={fieldInputStyle}
      {...props}
    />
  );

  return (
    <ModalHub titulo={`Dados operacionais · pedido ${pedido}`} icone="tim-icons icon-pencil" onClose={fechar} maxWidth={760}>
      {erroCarga ? <Aviso tom="erro">{erroCarga}</Aviso> : null}
      {carregando ? (
        <EstadoTabela loading textoCarregando="Carregando os dados do pedido..." />
      ) : (
        <div className="docs-frete__form">
          <Row>
            {campo("codigoRastreio", input("codigoRastreio", { type: "text", maxLength: 50 }), 6)}
            {campo("qtdVolumes", input("qtdVolumes", { type: "text", inputMode: "numeric", placeholder: "0" }), 3)}
            {campo("peso", input("peso", { type: "text", inputMode: "decimal", placeholder: "0,000" }), 3)}
          </Row>
          <Row>
            {campo("altura", input("altura", { type: "text", inputMode: "decimal" }), 4)}
            {campo("largura", input("largura", { type: "text", inputMode: "decimal" }), 4)}
            {campo("comprimento", input("comprimento", { type: "text", inputMode: "decimal" }), 4)}
          </Row>
          <Row>
            {campo("contatoNome", input("contatoNome", { type: "text", maxLength: 120 }), 5)}
            {campo("contatoTelefone", input("contatoTelefone", { type: "text", maxLength: 30 }), 3)}
            {campo("contatoEmail", input("contatoEmail", { type: "email", maxLength: 120 }), 4)}
          </Row>
          <Row>{campo("observacao", input("observacao", { type: "textarea", rows: 3, maxLength: 500 }), 12)}</Row>
          <span className="docs-frete__dica">Os dados valem para o pedido; as notas fiscais não mudam.</span>
        </div>
      )}

      {aviso ? <Aviso tom={aviso.tom}>{aviso.texto}</Aviso> : null}

      <div className="docs-frete__modal-rodape">
        <Button color="secondary" size="sm" onClick={fechar} disabled={salvando}>
          Fechar
        </Button>
        <Button color="primary" size="sm" onClick={salvar} disabled={salvando || carregando || Boolean(erroCarga)}>
          {salvando ? <Spinner size="sm" style={{ marginRight: 6 }} /> : null}
          {salvando ? "Salvando..." : "Salvar"}
        </Button>
      </div>
    </ModalHub>
  );
}
